import React from "react";
import { Check, Pencil, ScanSearch, Trash2, X } from "lucide-react";
import Tooltip from "../ui/Tooltip";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../../utils/inspectorChromeButtonClasses";

const INSPECT_BUTTON_CLASS = [
  INSPECTOR_CHROME_SECTION_ICON_CLASS,
  "h-7 w-7 !rounded-md !border-0 !bg-transparent text-gray-300",
  "hover:!bg-[var(--panel-surface-hover)] hover:text-gray-100",
].join(" ");

const InspectableMenuRow = React.memo(function InspectableMenuRow({
  children,
  inspectLabel,
  onInspect,
  renameValue = "",
  onRename = null,
  renameLabel = "Rename",
  onDelete = null,
  deleteLabel = "Delete",
  rightContent = null,
  rightContentClassName = "",
}) {
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [renameDraft, setRenameDraft] = React.useState("");
  const canRename = typeof onRename === "function";

  const cancelRename = React.useCallback(() => {
    setIsRenaming(false);
    setRenameDraft("");
  }, []);

  const commitRename = React.useCallback(() => {
    if (!canRename) return;
    const trimmedName = renameDraft.trim();
    if (trimmedName && trimmedName !== String(renameValue || "")) {
      onRename(trimmedName);
    }
    cancelRename();
  }, [canRename, cancelRename, onRename, renameDraft, renameValue]);

  const startRename = React.useCallback(() => {
    if (!canRename) return;
    setRenameDraft(String(renameValue || ""));
    setIsRenaming(true);
  }, [canRename, renameValue]);

  React.useEffect(() => {
    if (!isRenaming) return;
    setRenameDraft(String(renameValue || ""));
  }, [isRenaming, renameValue]);

  return (
    <div className="inspectable-menu-row group relative w-full flex items-center justify-between gap-2 rounded px-3 py-2 text-gray-200">
      <div className="inline-flex min-w-0 flex-1 items-center gap-2">
        <Tooltip content="Inspect" delayMs={500}>
          <button
            type="button"
            aria-label={inspectLabel}
            onClick={onInspect}
            className={`${INSPECT_BUTTON_CLASS} shrink-0`}
          >
            <ScanSearch size={15} aria-hidden="true" />
          </button>
        </Tooltip>
        {isRenaming ? (
          <input
            type="text"
            autoFocus
            value={renameDraft}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => setRenameDraft(event.target.value)}
            onBlur={commitRename}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitRename();
              } else if (event.key === "Escape") {
                event.preventDefault();
                cancelRename();
              }
            }}
            className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-900/90 px-2 py-0.5 text-sm text-gray-100 outline-none focus:border-gray-400"
            aria-label={renameLabel}
          />
        ) : (
          <div className="min-w-0 flex-1 truncate text-left select-none">{children}</div>
        )}
      </div>
      {!isRenaming && rightContent ? (
        <div className={`inline-flex shrink-0 items-center ${rightContentClassName}`.trim()}>
          {rightContent}
        </div>
      ) : null}
      {isRenaming ? (
        <>
          <Tooltip content="Save name">
            <button
              type="button"
              aria-label={`Save ${renameValue} name`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                commitRename();
              }}
              className="shrink-0 rounded p-0.5 text-gray-300 transition-colors duration-150 hover:text-green-300 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
            >
              <Check className="h-3 w-3" aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip content="Cancel rename">
            <button
              type="button"
              aria-label={`Cancel renaming ${renameValue}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                cancelRename();
              }}
              className="shrink-0 rounded p-0.5 text-gray-300 transition-colors duration-150 hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </Tooltip>
        </>
      ) : (
        <div className="inline-flex shrink-0 items-center gap-0.5">
          {canRename ? (
            <Tooltip content="Rename">
              <button
                type="button"
                aria-label={renameLabel}
                onClick={(event) => {
                  event.stopPropagation();
                  startRename();
                }}
                className="shrink-0 rounded p-0.5 text-gray-400 opacity-0 pointer-events-none transition-colors duration-150 hover:text-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 group-hover:opacity-100 group-hover:pointer-events-auto"
              >
                <Pencil className="h-3 w-3" aria-hidden="true" />
              </button>
            </Tooltip>
          ) : null}
          {onDelete ? (
            <Tooltip content="Delete">
              <button
                type="button"
                aria-label={deleteLabel}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete();
                }}
                className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-gray-400 opacity-0 pointer-events-none transition-colors duration-150 hover:text-red-400 focus:outline-none focus-visible:outline-none group-hover:opacity-100 group-hover:pointer-events-auto"
              >
                <Trash2 className="w-3 h-3" aria-hidden="true" />
              </button>
            </Tooltip>
          ) : null}
        </div>
      )}
    </div>
  );
});

export default InspectableMenuRow;
